import { Button, Typography } from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter
} from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import moment from "moment/moment";
import _uniqueId from "lodash/uniqueId";
import {formatMoney, formatNumbersToLongFormat, formatToLakhsCrores, useSharedStyles} from "../util/constant";
import {postData} from "../util/__fetch";

const Positions = ({ loggedInUser, selectedUserId, source, straddles }) => {
  const [positions, setPositions] = useState([])
  const [refreshStartTime, setRefreshStartTime] = useState(moment())
  const [pnl, setPnl] = useState(0)
  const [sodAvailableCash, setSodAvailableCash] = useState(0)
  const [usedCash, setUsedCash] = useState(0)
  const classes = useSharedStyles();

  const squareOffPosition = (tradingSymbol, symbolToken, netQty, exchange) => {
    const requestBody = {
      tradingSymbol: tradingSymbol,
      symbolToken: symbolToken,
      netQty: netQty,
      exchange: exchange,
    }
    postData('/algo-trading/api/fno/square-off-position/'+selectedUserId, loggedInUser, requestBody, (() => {}), (() => {}), (() => {}));
  }

  const COLUMNS = [
    { field: 'tradingsymbol', headerName: 'Symbol', width: 200, type: 'string', },
    { field: 'expirydate', headerName: 'Expiry', width: 100, type: 'string', },
    { field: 'buyamount', headerName: 'Buy Amount', width: 120, type: 'number',
    valueFormatter: ({ value }) => {
        return value ? formatMoney(value, false) : '';
    }
    },
    { field: 'buyqty', headerName: 'Buy Qty', width: 80, type: 'number', },
    { field: 'sellamount', headerName: 'Sell Amount', width: 120, type: 'number',
      valueFormatter: ({ value }) => {
        return value ? formatMoney(value, false) : '';
      }
    },
    { field: 'sellqty', headerName: 'Sell Qty', width: 80, type: 'number', },
    { field: 'netqty', headerName: 'Net Qty', width: 80, type: 'number', },
    { field: 'pnl', headerName: 'P&L', width: 80, type: 'number',
      valueFormatter: ({ value }) => {
        return value ? formatMoney(value) : '';
      }
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      renderCell: ({ row }) =>
        <Button onClick={() => squareOffPosition(row?.tradingsymbol, row?.symboltoken, row?.netqty, row?.exchange)}>
          Square Off
        </Button>,
    },
  ]

  function getPositions() {
    fetch("/algo-trading/api/fno/portfolio/available-cash?userId=" + selectedUserId, {
      headers: new Headers({
        'Authorization': loggedInUser.credential
      }),
    }
    ).then(r => {
      if (r.status >= 400) {
        return
      }
      r.text()
        .then(value => {
          if (!value) {
            return;
          }
          const portfolioAmounts = JSON.parse(value);
          setSodAvailableCash(portfolioAmounts?.totalAmount.toFixed(0))
          setUsedCash(portfolioAmounts?.usedAmount.toFixed(0))
        })
    }).catch(error => {
      console.log(error);
    });
    const endpoint = '/algo-trading/api/demat/positions?userId=' + selectedUserId;
    function getNumber(value) {
      return value ? Number(value) : 0;
    }
    fetch(endpoint, {
      headers: new Headers({
        'Authorization': loggedInUser.credential
      }),
    }
    ).then(r => {
      if (r.status >= 400) {
        return
      }
      r.text()
        .then(value => {
          const positionsJSON = JSON.parse(value);
          if (source === 'fno') {
            const filteredPositions = positionsJSON
              .filter(position => position?.netqty !== 0)
              .filter(position => position?.exchange === 'NFO' || position?.exchange === 'BFO');
            setPositions(filteredPositions);
            const pnl = positionsJSON
              .filter(position => position?.exchange === 'NFO' || position?.exchange === 'BFO')
              .reduce((acc, position) => {
                return acc + (getNumber(position?.pnl)  || 0);
              }, 0);
            setPnl(pnl?.toFixed(2));
          }
          else {
            setPositions(positionsJSON);
            const pnl = positionsJSON
              .filter(position => position?.netqty === 0)
              .reduce(function (acc, cur) {
                 return acc + (getNumber(cur.pnl) || 0);
              }, 0);
            setPnl(pnl?.toFixed(2));
          }
        })
    }
    ).catch(error => {
      console.log(error);
    });
  }

  useEffect(() => {
    getPositions();
  }, [selectedUserId, refreshStartTime]);

  const startRefresh = () => {
    setRefreshStartTime(moment())
  }

  useEffect(() => {
    let interval;
    if (source === 'fno' && straddles && straddles?.length > 0) {
      interval = setInterval(() => {
        getPositions()
      }, 3000);
    }
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, []);

  return (
    <>
      <DataGrid
        getRowId={(row) => row?.tradingsymbol + _uniqueId()}
        columns={COLUMNS}
        rows={positions}
        autoHeight={true}
        initialState={{
          sorting: {
            sortModel: [{ field: 'tradingsymbol', sort: 'asc' }]
          },
          pagination: {
            paginationModel: { page: 0, pageSize: 50 },
          },
        }}
        pageSizeOptions={[50, 100, 150, 200]}
        getCellClassName={(params) => {
          if (params.field === 'pnl') {
            return Number(params?.value) > 0 ? classes.profit : classes.loss;
          }
          return '';
        }}
        slots={{
          toolbar: () => <GridToolbarContainer style={{ justifyContent: 'space-around' }}>
            <Typography variant='h6' align='center'>{source === 'fno' ? selectedUserId : ''} Positions</Typography>
            <Typography color={pnl > 0 ? "green" : "red"}>{formatNumbersToLongFormat(pnl)} ({(100 * pnl / sodAvailableCash).toFixed(2)}%)</Typography>
            <Typography>({formatToLakhsCrores(usedCash)}/{formatToLakhsCrores(sodAvailableCash)})</Typography>
            <>
              <Button onClick={startRefresh}>Refresh</Button>
              <GridToolbarFilterButton />
              <GridToolbarExport />
              <GridToolbarQuickFilter />
            </>
          </GridToolbarContainer>
        }}
        sx={{ bgcolor: 'white' }}
      />
    </>
  )
}

export default Positions